import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import {
  HiOutlineXMark,
  HiOutlineCheckCircle,
  HiOutlinePencilSquare,
  HiOutlineCalendarDays,
  HiOutlineMagnifyingGlass,
} from "react-icons/hi2";

const ActividadesPendientes = () => {
  const [loading, setLoading] = useState(true);
  const [pendientes, setPendientes] = useState([]);
  const [open, setOpen] = useState(false);
  const [selectedPendiente, setSelectedPendiente] = useState(null);
  const [search, setSearch] = useState("");
  const [filtro, setFiltro] = useState("con_pendientes"); // con_pendientes | resueltos | todos

  const handleOpenModal = (pendiente) => { setSelectedPendiente(pendiente); setOpen(true); };
  const handleClose = () => { setOpen(false); setSelectedPendiente(null); };

  useEffect(() => { fetchActividades(); }, []);

  const estaResuelto = (pendiente) =>
    pendiente.reportes_pendientes.length > 0 &&
    pendiente.reportes_pendientes.every((rp) => rp.resuelto);

  const marcarResuelto = async (reporteId) => {
    try {
      await axios.put(`https://www.desarrollotecnologicoar.com/api5/resolver_pendientes/${reporteId}`);

      let pendienteActualizado = null;

      // Refleja el cambio de inmediato sin recargar la pagina
      setPendientes((prev) =>
        prev.map((p) => {
          if (!p.reportes_pendientes.some((rp) => rp.id === reporteId)) return p;
          const actualizado = {
            ...p,
            reportes_pendientes: p.reportes_pendientes.map((rp) =>
              rp.id === reporteId ? { ...rp, resuelto: true } : rp
            ),
          };
          pendienteActualizado = actualizado;
          return actualizado;
        })
      );
      setSelectedPendiente((prev) =>
        prev
          ? {
              ...prev,
              reportes_pendientes: prev.reportes_pendientes.map((rp) =>
                rp.id === reporteId ? { ...rp, resuelto: true } : rp
              ),
            }
          : prev
      );

      // Si con esto quedaron resueltos TODOS los pendientes, dispara el
      // handshake de encuesta de satisfacción (bandeja de entrada del vendedor).
      if (pendienteActualizado && estaResuelto(pendienteActualizado)) {
        dispararHandshake(pendienteActualizado);
      }
    } catch (err) { console.error(err); }
  };

  /// No bloquea el flujo si falla: solo se registra en consola.
  const dispararHandshake = async (pendiente) => {
    try {
      await axios.post("https://desarrollotecnologicoar.com/api19/handshakes", {
        ticket: pendiente.ticket,
        tecnico_nombre: pendiente.nombre_tecnico_asignado,
        tecnico_telefono: pendiente.tecnico_telefono,
        cliente_nombre: pendiente.contacto,
        cliente_telefono: pendiente.telefono,
        empresa: pendiente.empresa,
        motivo_trigger: "PENDIENTE_RESUELTO",
        url_ultimo_pdf: pendiente.url_pdf_generado,
      });
    } catch (err) {
      console.error("No se pudo disparar el handshake:", err);
    }
  };


  const fetchActividades = async () => {
    setLoading(true);
    try {
      const response = await axios.get("https://desarrollotecnologicoar.com/api5/reservas_con_pendientes");
      const actividadesData = response.data;
      const actividadesReserva = await Promise.all(
        actividadesData.map(async (actividad) => {
          const reservaResponse = await axios.get(`https://desarrollotecnologicoar.com/api5/reserva_id/${actividad.reporte_id}`);
          return reservaResponse.data;
        })
      );
      setPendientes(actividadesReserva);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const calcularDias = (fecha) => Math.floor((new Date() - new Date(fecha)) / (1000 * 60 * 60 * 24));

  const colorDias = (dias) => {
    if (dias < 3) return "text-emerald-600";
    if (dias < 7) return "text-amber-500";
    return "text-red-600";
  };

  const pendientesOrdenados = useMemo(
    () => [...pendientes].sort((a, b) => new Date(a.reportes_pendientes[0]?.created_at) - new Date(b.reportes_pendientes[0]?.created_at)),
    [pendientes]
  );

  const conteos = useMemo(() => {
    const resueltos = pendientesOrdenados.filter(estaResuelto).length;
    return {
      todos: pendientesOrdenados.length,
      con_pendientes: pendientesOrdenados.length - resueltos,
      resueltos,
    };
  }, [pendientesOrdenados]);

  const pendientesFiltrados = useMemo(() => {
    const term = search.trim().toLowerCase();
    return pendientesOrdenados.filter((p) => {
      const resuelto = estaResuelto(p);
      if (filtro === "con_pendientes" && resuelto) return false;
      if (filtro === "resueltos" && !resuelto) return false;

      if (!term) return true;
      const ticket = (p.ticket || "").toString().toLowerCase();
      const tecnico = (p.nombre_tecnico_asignado || "").toString().toLowerCase();
      return ticket.includes(term) || tecnico.includes(term);
    });
  }, [pendientesOrdenados, filtro, search]);

  if (loading) {
    return (
      <div className="page-container flex justify-center py-20">
        <svg className="w-8 h-8 animate-spin text-slate-400" viewBox="0 0 24 24" fill="none"><circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" /><path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" /></svg>
      </div>
    );
  }

  const tabs = [
    { key: "con_pendientes", label: "Con pendientes" },
    { key: "resueltos", label: "Resueltos" },
    { key: "todos", label: "Todos" },
  ];

  return (
    <div className="page-container">
      <h1 className="page-title text-center mb-6">Actividades Pendientes</h1>

      {/* Buscador */}
      <div className="relative mb-4">
        <HiOutlineMagnifyingGlass className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar por ticket o tecnico..."
          className="w-full pl-10 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
        />
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-4">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFiltro(tab.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-semibold transition-colors ${
              filtro === tab.key ? "bg-red-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
            }`}
          >
            {tab.label} <span className="ml-1 opacity-80">{conteos[tab.key]}</span>
          </button>
        ))}
      </div>

      {/* Tabla */}
      <div className="card-hover overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 text-xs uppercase border-b border-slate-100">
              <th className="p-3">Ticket</th>
              <th className="p-3">Tecnico</th>
              <th className="p-3">Pendientes</th>
              <th className="p-3">Estatus</th>
              <th className="p-3">Antiguedad</th>
              <th className="p-3">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {pendientesFiltrados.map((pendiente) => {
              const dias = calcularDias(pendiente.reportes_pendientes[0]?.created_at);
              const resuelto = estaResuelto(pendiente);
              return (
                <tr
                  key={pendiente.id}
                  onClick={() => handleOpenModal(pendiente)}
                  className="border-b border-slate-50 hover:bg-slate-50 cursor-pointer"
                >
                  <td className="p-3 font-bold text-slate-900">#{pendiente.ticket}</td>
                  <td className="p-3 text-slate-700">{pendiente.nombre_tecnico_asignado}</td>
                  <td className="p-3 text-slate-700">{pendiente.reportes_pendientes.length}</td>
                  <td className="p-3">
                    <span
                      className={`px-2 py-0.5 rounded-full text-xs font-semibold ${
                        resuelto ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"
                      }`}
                    >
                      {resuelto ? "Resuelto" : "Con pendientes"}
                    </span>
                  </td>
                  <td className={`p-3 font-semibold ${colorDias(dias)}`}>{dias} dias</td>
                  <td className="p-3">
                    <Link
                      to={`/editar_solicitud/${pendiente.id}/aprobados`}
                      onClick={(e) => e.stopPropagation()}
                      className="text-xs py-1.5 px-3 inline-flex items-center gap-1.5 bg-blue-600 text-white rounded-btn font-semibold hover:bg-blue-700 transition-colors"
                    >
                      <HiOutlinePencilSquare className="w-4 h-4" /> Editar Reserva
                    </Link>
                  </td>
                </tr>
              );
            })}
            {pendientesFiltrados.length === 0 && (
              <tr>
                <td colSpan={6} className="p-6 text-center text-slate-400">No hay resultados.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {open && (
        <>
          <div className="overlay" onClick={handleClose} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="modal w-full max-w-lg max-h-[80vh] overflow-y-auto text-center">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-slate-900">Detalles de Actividades Pendientes</h3>
                <button onClick={handleClose} className="btn-icon"><HiOutlineXMark className="w-5 h-5" /></button>
              </div>

              {selectedPendiente?.itinerario?.length > 0 && (
                <div className="mb-4 text-left">
                  <p className="text-xs uppercase font-semibold text-slate-400 flex items-center gap-1 mb-1.5">
                    <HiOutlineCalendarDays className="w-4 h-4" /> Itinerario
                  </p>
                  <div className="space-y-1 max-h-32 overflow-y-auto">
                    {selectedPendiente.itinerario.map((dia) => (
                      <p key={dia.id} className="text-sm text-slate-700">
                        {new Date(dia.fecha).toLocaleDateString()} &middot; {dia.entrada} - {dia.salida}
                      </p>
                    ))}
                  </div>
                </div>
              )}

              {selectedPendiente?.reportes_pendientes?.length > 0 ? (
                <div className="space-y-3 text-left">
                  {selectedPendiente.reportes_pendientes.map((reporte) => (
                    <div key={reporte.id} className={`p-3 rounded-lg border ${reporte.resuelto ? "bg-green-50 border-green-200" : "bg-red-50 border-red-200"}`}>
                      <p className="font-semibold text-slate-800">{reporte.asunto}</p>
                      <p className="text-xs text-slate-500 mt-1">{new Date(reporte.created_at).toLocaleString()}</p>
                      {reporte.fotos?.length > 0 && (
                        <div className="mt-2 space-y-2">
                          {reporte.fotos.map((foto, idx) => (
                            <img key={idx} src={foto.url_img} alt={foto.tag} className="w-3/4 mx-auto rounded-lg" />
                          ))}
                        </div>
                      )}
                      {reporte.resuelto ? (
                        <p className="text-emerald-600 font-semibold mt-2 text-center">Resuelto</p>
                      ) : (
                        <div className="text-center mt-2">
                          <button onClick={() => marcarResuelto(reporte.id)} className="btn-danger text-xs py-1.5 px-3">
                            <HiOutlineCheckCircle className="w-4 h-4" /> Marcar como resuelto
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-slate-400 text-sm">No hay actividades registradas.</p>
              )}

              <button onClick={handleClose} className="btn-primary mt-4">Cerrar</button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ActividadesPendientes;
